import {
  IQuestionnaireQuestionRaw,
  QuestionnaireAnswer,
  QuestionnaireResult,
  QuestionnaireResultEntry,
  QuestionnaireStepQuestion,
} from "./types";
import { serializeRawQuestion } from "./serializer";

export const normalizeQuestionnaireQuestions = (
  questionnaireQuestions?: IQuestionnaireQuestionRaw[]
): QuestionnaireStepQuestion[] => {
  if (!questionnaireQuestions || questionnaireQuestions.length === 0) {
    return [];
  }

  return [...questionnaireQuestions]
    .sort((a, b) => a.order - b.order)
    .map(serializeRawQuestion);
};

export const getAnswerSelectedValues = (answer?: QuestionnaireAnswer): string[] => {
  if (Array.isArray(answer?.value)) {
    return answer.value;
  }

  if (typeof answer?.value === "string" && answer.value.trim().length > 0) {
    return [answer.value];
  }

  return [];
};

export const getAnswerSelectedTexts = (answer?: QuestionnaireAnswer): string[] => {
  if (Array.isArray(answer?.text)) {
    return answer.text;
  }

  if (typeof answer?.text === "string" && answer.text.trim().length > 0) {
    return [answer.text];
  }

  return [];
};

export const isCurrentQuestionValid = (question: QuestionnaireStepQuestion, answer?: QuestionnaireAnswer) => {
  const selectedValues = getAnswerSelectedValues(answer);
  const availableOptionValues = new Set(question.options.map((option) => option.value));
  const selectedKnownValues = selectedValues.filter((value) => availableOptionValues.has(value));

  const selectedOtherOption = question.options.find((option) => option.is_other && selectedKnownValues.includes(option.value));
  const hasValidOtherText = !selectedOtherOption || Boolean(answer?.otherText?.trim());

  if (question.answer_type === "multiple_choice") {
    return selectedKnownValues.length > 0 && hasValidOtherText;
  }

  if (question.answer_type === "free_text") {
    return typeof answer?.value === "string" && answer.value.trim().length > 0;
  }

  if (typeof answer?.value !== "string") {
    return false;
  }

  const normalizedValue = answer.value.trim();
  return normalizedValue.length > 0 && availableOptionValues.has(normalizedValue) && hasValidOtherText;
};

export const hasAnswerForNavigation = (question: QuestionnaireStepQuestion, answer?: QuestionnaireAnswer) => {
  if (!question.mandatory) {
    return true;
  }
  return isCurrentQuestionValid(question, answer);
};

const getFirstAnsweredDisplayValue = (result: QuestionnaireResult): string => {
  const entry = Object.values(result).find((a) => {
    if (a?.text) return Array.isArray(a.text) ? a.text.length > 0 : a.text.trim().length > 0;
    if (a?.value != null) return Array.isArray(a.value) ? a.value.length > 0 : String(a.value).trim().length > 0;
    return false;
  });

  if (!entry) return '';
  const { text, value } = entry;
  if (text) return Array.isArray(text) ? text.join(', ') : text;
  if (value != null) return Array.isArray(value) ? value.join(', ') : String(value);
  return '';
};

export const getQuestionnaireDisplayState = (result: QuestionnaireResult) => {
  const entries = Object.values(result);
  const questionCount = entries.length;

  const numericValues = entries
    .map(e => (typeof e.value === 'number' ? e.value : null))
    .filter((v): v is number => v !== null);
  const totalScore =
    numericValues.length === questionCount && questionCount > 0
      ? numericValues.reduce((sum, v) => sum + v, 0)
      : null;

  if (questionCount > 1) {
    return {
      showViewAnswersButton: true,
      displayValue: totalScore !== null ? String(totalScore) : '',
      shouldShowValue: totalScore !== null,
    };
  }

  const displayValue =
    totalScore !== null ? String(totalScore) : getFirstAnsweredDisplayValue(result);
  return {
    showViewAnswersButton: questionCount > 0,
    displayValue,
    shouldShowValue: displayValue.length > 0,
  };
};

export const buildQuestionnaireAnswers = (
  questions: QuestionnaireStepQuestion[],
  answersByQuestionId: Record<string, QuestionnaireAnswer>
): Array<{ question_id: string; selected_option_ids: string[]; free_text: string | null }> => {
  return questions.map((question) => {
    const answer = answersByQuestionId[question.id];

    if (question.answer_type === 'free_text') {
      return {
        question_id: question.id,
        selected_option_ids: [],
        free_text: typeof answer?.value === 'string' ? answer.value : null,
      };
    }

    const selectedValues = Array.isArray(answer?.value)
      ? answer.value
      : typeof answer?.value === 'string' && answer.value
      ? [answer.value]
      : [];

    const selected_option_ids = question.options
      .filter((opt) => selectedValues.includes(opt.value) && opt.id)
      .map((opt) => opt.id as string);

    const otherOption = question.options.find((opt) => opt.is_other && selectedValues.includes(opt.value));
    const free_text = otherOption ? (answer?.otherText ?? null) : null;

    return { question_id: question.id, selected_option_ids, free_text };
  });
};

export const getQuestionAnswerPayload = (answer?: QuestionnaireAnswer): QuestionnaireResultEntry => {
  if (!answer) {
    return {
      value: null,
      text: null,
    };
  }

  if (Array.isArray(answer.value)) {
    const values = answer.value.filter((item) => String(item).trim().length > 0);
    const text = Array.isArray(answer.text)
      ? answer.text.filter((item) => item.trim().length > 0)
      : [];

    return {
      value: values,
      text,
    };
  }

  const stringValue = typeof answer.value === "string" ? answer.value.trim() : "";
  const stringText = typeof answer.text === "string" ? answer.text.trim() : "";

  return {
    value: stringValue || null,
    text: stringText || stringValue || null,
  };
};
